import type { ContentVisibility, IEntityService } from "@brains/plugins";
import { TOPIC_ENTITY_TYPE } from "./constants";
import { getTopicTitle } from "./topic-presenter";

const MAX_EXISTING_TOPIC_TITLES = 40;

/**
 * Fetch a small set of existing topic titles so extraction can reuse
 * canonical names instead of inventing near-duplicates.
 */
export async function listExistingTopicTitles(
  entityService: IEntityService,
  limit: number = MAX_EXISTING_TOPIC_TITLES,
  targetVisibility?: ContentVisibility,
): Promise<string[]> {
  const topics = await entityService.listEntities({
    entityType: TOPIC_ENTITY_TYPE,
    options:
      targetVisibility === undefined
        ? { limit }
        : { filter: { visibilityScope: targetVisibility } },
  });

  return topics
    .filter(
      (topic) =>
        targetVisibility === undefined || topic.visibility === targetVisibility,
    )
    .map(getTopicTitle)
    .filter((title): title is string => title.trim().length > 0)
    .slice(0, limit);
}

/**
 * Build the extraction prompt with canonicalization guidance.
 */
export function buildTopicExtractionPrompt(params: {
  entityTitle: string;
  entityType: string;
  content: string;
  existingTopicTitles?: string[];
}): string {
  const existingTitles = (params.existingTopicTitles ?? []).slice(
    0,
    MAX_EXISTING_TOPIC_TITLES,
  );

  const existingTopicsBlock =
    existingTitles.length > 0
      ? `

Existing topic titles to reuse when they clearly fit:
${existingTitles.map((title) => `- ${title}`).join("\n")}`
      : "";

  return `Content Title: ${params.entityTitle}
Content Type: ${params.entityType}

TOPIC GRANULARITY CONTRACT:
- A topic is a durable knowledge domain expected to accumulate multiple source entities over time.
- Create topics for stable concepts, domains, practices, or enduring questions.
- Create topics for durable domains, never operational activities, deliverables, launch steps, staging deployments, landing page copy, todos, action plans, or one-off tasks.
- At small corpus sizes, prefer fewer broader topics over narrow artifacts.

CANONICALIZATION RULES:
- Prefer reusable umbrella topics over article-specific framings, arguments, or sub-angles.
- Reuse an existing topic title exactly when the content clearly belongs to it.
- If multiple possible titles describe the same core idea, choose the broadest stable title and move the nuance into the description and content.
- Avoid creating near-duplicates that only differ by actor naming, qualifiers, or rhetorical framing.
- Prefer the base concept over a framed version like "X as Y", "X in Z", or "Angle on X" when they point to the same reusable topic.

Examples of good grouping:
- "Agency in Human-AI Collaboration", "Human-Agent Collaboration", and "AI Collaboration" should usually collapse to one canonical collaboration topic.
- Titles like "Fragmentation as Opportunity" or "Fragmentation in Digital Ecosystems" should usually collapse to the umbrella topic "Fragmentation".
- "Fragmentation and Multiplicity" should usually also collapse to "Fragmentation" when multiplicity is just the angle, not a separate durable domain.
- "Regenerative and Decentralized Design" should usually become the single primary design topic, with the secondary lens moved into the topic content.${existingTopicsBlock}

Content:
${params.content}`;
}
